<script setup lang="ts">
import { ref } from 'vue'
import { useSupabaseAuth } from '../composables/useSupabaseAuth'
import { useI18n } from 'vue-i18n'
import { X } from 'lucide-vue-next'

const { t } = useI18n()

const props = defineProps<{
  isOpen: boolean
}>()

const emit = defineEmits<{
  close: []
}>()

const { signInWithGoogle, signInWithApple, signInWithEmail } = useSupabaseAuth()

const emailInput = ref('')
const isEmailMode = ref(false)
const emailSent = ref(false)
const isLoading = ref(false)
const error = ref<string | null>(null)

async function handleGoogleSignIn() {
  error.value = null
  isLoading.value = true
  try {
    await signInWithGoogle()
  } catch (e) {
    error.value = t('auth.googleSignInFailed')
  } finally {
    isLoading.value = false
  }
}

async function handleAppleSignIn() {
  error.value = null
  isLoading.value = true
  try {
    console.log('[Auth] Starting Apple Sign-In...')
    await signInWithApple()
    console.log('[Auth] Apple Sign-In redirect initiated')
  } catch (e: any) {
    console.error('[Auth] Apple Sign-In error:', e)
    console.error('[Auth] Error stack:', e?.stack)
    error.value = t('auth.appleSignInFailed')
  } finally {
    isLoading.value = false
  }
}

async function handleEmailSignIn() {
  if (!emailInput.value || !emailInput.value.includes('@')) {
    error.value = t('auth.invalidEmail')
    return
  }

  error.value = null
  isLoading.value = true
  try {
    await signInWithEmail(emailInput.value)
    emailSent.value = true
  } catch (e) {
    error.value = t('auth.magicLinkFailed')
  } finally {
    isLoading.value = false
  }
}

function resetModal() {
  isEmailMode.value = false
  emailSent.value = false
  emailInput.value = ''
  error.value = null
}

function closeModal() {
  resetModal()
  emit('close')
}
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="isOpen"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
        @click.self="closeModal"
      >
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black/50 backdrop-blur-sm" />

        <!-- Modal -->
        <div class="relative w-full max-w-sm bg-white dark:bg-dark-elevated rounded-2xl shadow-2xl overflow-hidden">
          <!-- Close button -->
          <button
            @click="closeModal"
            class="absolute top-4 right-4 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors z-10"
          >
            <X class="w-5 h-5" />
          </button>

          <!-- Content -->
          <div class="p-8">
            <!-- Header -->
            <div class="text-center mb-8">
              <div class="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 dark:from-ios-blue dark:to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
              </div>
              <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
                {{ emailSent ? $t('auth.checkEmail') : $t('auth.welcome') }}
              </h2>
              <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {{ emailSent ? $t('auth.magicLinkSent') : $t('auth.signInToContinue') }}
              </p>
            </div>

            <!-- Email sent confirmation -->
            <div v-if="emailSent" class="text-center">
              <div class="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg class="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <p class="text-sm text-gray-600 dark:text-gray-300 mb-6">
                {{ $t('auth.clickEmailLink') }}<br/>
                <span class="text-gray-400 dark:text-gray-500">{{ emailInput }}</span>
              </p>
              <button
                @click="resetModal"
                class="text-sm text-blue-600 dark:text-ios-blue hover:underline"
              >
                {{ $t('auth.useDifferentMethod') }}
              </button>
            </div>

            <!-- Email input mode -->
            <div v-else-if="isEmailMode" class="space-y-4">
              <div>
                <input
                  v-model="emailInput"
                  type="email"
                  :placeholder="$t('auth.enterEmail')"
                  class="w-full px-4 py-3 bg-gray-50 dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-ios-blue focus:border-transparent transition-all"
                  @keyup.enter="handleEmailSignIn"
                  :disabled="isLoading"
                />
              </div>

              <button
                @click="handleEmailSignIn"
                :disabled="isLoading || !emailInput"
                class="w-full py-3 bg-blue-600 dark:bg-ios-blue hover:bg-blue-700 dark:hover:bg-blue-500 text-white font-medium rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <svg v-if="isLoading" class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>{{ isLoading ? $t('common.sending') : $t('auth.sendMagicLink') }}</span>
              </button>

              <button
                @click="isEmailMode = false"
                class="w-full text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
              >
                {{ $t('auth.backToOptions') }}
              </button>
            </div>

            <!-- Main sign-in options -->
            <div v-else class="space-y-3">
              <!-- Google -->
              <button
                @click="handleGoogleSignIn"
                :disabled="isLoading"
                class="w-full py-3 px-4 bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-elevated rounded-xl font-medium text-gray-700 dark:text-gray-200 transition-all flex items-center justify-center gap-3 disabled:opacity-50"
              >
                <svg class="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>{{ $t('auth.continueWithGoogle') }}</span>
              </button>

              <!-- Apple -->
              <button
                @click="handleAppleSignIn"
                :disabled="isLoading"
                class="w-full py-3 px-4 bg-black dark:bg-white hover:bg-gray-900 dark:hover:bg-gray-100 rounded-xl font-medium text-white dark:text-black transition-all flex items-center justify-center gap-3 disabled:opacity-50"
              >
                <svg class="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                <span>{{ $t('auth.continueWithApple') }}</span>
              </button>

              <!-- Divider -->
              <div class="relative my-6">
                <div class="absolute inset-0 flex items-center">
                  <div class="w-full border-t border-gray-200 dark:border-dark-border"></div>
                </div>
                <div class="relative flex justify-center text-sm">
                  <span class="px-4 bg-white dark:bg-dark-elevated text-gray-400 dark:text-gray-500">{{ $t('common.or') }}</span>
                </div>
              </div>

              <!-- Email -->
              <button
                @click="isEmailMode = true"
                :disabled="isLoading"
                class="w-full py-3 px-4 bg-gray-50 dark:bg-dark-surface border border-gray-200 dark:border-dark-border hover:bg-gray-100 dark:hover:bg-dark-elevated rounded-xl font-medium text-gray-700 dark:text-gray-200 transition-all flex items-center justify-center gap-3 disabled:opacity-50"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span>{{ $t('auth.continueWithEmail') }}</span>
              </button>
            </div>

            <!-- Error message -->
            <p v-if="error" class="mt-4 text-sm text-red-500 dark:text-red-400 text-center">
              {{ error }}
            </p>

            <!-- Footer -->
            <p class="mt-6 text-xs text-gray-400 dark:text-gray-500 text-center">
              {{ $t('auth.byContinuing') }}
              <a href="/privacy" class="underline hover:text-gray-600 dark:hover:text-gray-300">{{ $t('footer.privacyPolicy') }}</a>
            </p>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: all 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .relative,
.modal-leave-to .relative {
  transform: scale(0.95) translateY(10px);
}
</style>
